import React, { Fragment } from 'react';
import { MsalAuthenticationTemplate, useAccount } from '@azure/msal-react';
import Providers from './Providers';
import GlobalStyles from './components/GlobalStyles';
import { createTheme, StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles';
import Router from './Router';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from './auth-config';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { Provider } from '@rollbar/react';
import { Configuration } from 'rollbar';
import LoadingSpinner from './components/LoadingSpinner';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#0828CC',
      light: '#663CFF',
      dark: '#00009A',
    },
  },
  typography: {
    fontWeightMedium: 700,
    button: {
      fontWeight: 600,
      fontSize: '0.75rem',
      letterSpacing: '1px',
    },
  },
});

const App = () => {
  const account = useAccount();
  const user = account?.idTokenClaims;

  const rollbarConfig: Configuration = {
    accessToken: ROLLBAR_ACCESS_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: ENVIRONMENT.toLowerCase(),
    code_version: COMMITHASH,
    enabled: ENVIRONMENT === 'Production',
    logLevel: 'error',
    payload: {
      client: {
        javascript: {
          source_map_enabled: true,
        },
      },
      person: {
        id: null,
        firstname: user?.given_name,
        lastname: user?.family_name,
        username: user?.name,
        email: user?.email as string,
      },
    },
  };

  return (
    <Fragment>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        errorComponent={ErrorComponent}
        loadingComponent={LoadingComponent}
      >
        <Provider config={rollbarConfig}>
          <Providers>
            <GlobalStyles />
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Router />
                </LocalizationProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </Providers>
        </Provider>
      </MsalAuthenticationTemplate>
    </Fragment>
  );
};

export default App;

function ErrorComponent({ error }) {
  return <p>An Error Occurred: {error}</p>;
}

function LoadingComponent() {
  return <LoadingSpinner message="Authentication in progress..." />;
}
